<template>
  <div class="thumbnail-picker">
    <div class="thumbnail-picker__header mb-2">
      <strong class="bhGray2--text">{{ label }}</strong>
    </div>

    <div class="thumbnail-picker__details">
      <v-list-item-avatar
        width="64"
        height="64"
        :class="
          `thumbnail-picker__thumbnail mr-2 ${
            isFileSelected ? 'thumbnail-picker__thumbnail--selected' : ''
          }`
        "
      >
        <v-img
          :src="filePreview"
          @onError="onFilePreviewError"
          @load="onFilePreviewLoad"
          @click="showPreview"
        >
          <template v-if="isFileLoaded">
            <div class="d-flex align-center fill-height flex-1 justify-center">
              <v-progress-circular
                indeterminate
                color="accent"
              ></v-progress-circular>
            </div>
          </template>
          <v-icon
            v-if="isVideoType && showAction"
            size="24"
            color="bhWhite"
            class="thumbnail-picker__play-icon"
            >bhi-play-circle</v-icon
          >
        </v-img>
      </v-list-item-avatar>
      <v-btn
        @click="removeFile"
        icon
        v-if="isFileSelected && !readonly"
        class="mr-2"
      >
        <v-icon>bhi-trash</v-icon>
      </v-btn>
      <v-menu
        v-if="!readonly"
        offset-y
        origin="center center"
        :nudge-bottom="10"
        transition="scale-transition"
        class="thumbnail-picker__add-media-menu"
      >
        <template v-slot:activator="{ on }">
          <v-btn elevation="0" color="accent" rounded v-on="on" outlined>
            <template v-if="isFileSelected">
              <span>Change file</span>
            </template>
            <template v-else>
              <v-icon class="mr-2">mdi-plus</v-icon><span>Add file</span>
            </template>
          </v-btn>
        </template>
        <v-list class="pa-0">
          <v-list-item class="px-0 thumbnail-picker__add-media-menu__list-item">
            <v-btn
              text
              color="accent"
              class="rounded-0 thumbnail-picker__action-button"
              elevation="0"
              @click="openMlFilePickerDialog()"
              :disabled="uploadFile.length > 0 && !oneSourceOnly ? true : false"
            >
              <v-icon class="mr-1">bhi-book-open</v-icon
              ><span>Add from library</span>
            </v-btn>
          </v-list-item>
          <v-list-item class="px-0 thumbnail-picker__add-media-menu__list-item">
            <file-upload
              :multiple="false"
              :input-id="`thumbnail-picker-${componentId}`"
              :class="
                `thumbnail-picker__action-button thumbnail-picker__action-button--upload ${
                  mlFile ? 'disabled' : ''
                }`
              "
              @input-file="inputFile"
              v-model="uploadFile"
              :accept="getMimeTypes(allowedFileTypes)"
              :ref="`upload-${componentId}`"
            >
              <v-btn
                class="rounded-0 thumbnail-picker__action-button"
                elevation="0"
                color="accent"
                text
                @click="() => selectLocalFile(`upload-${componentId}`)"
              >
                <v-icon class="mr-1">bhi-upload</v-icon><span>Upload file</span>
              </v-btn>
            </file-upload>
          </v-list-item>
          <v-list-item
            v-if="enableMobileStaticMedia"
            class="px-0 thumbnail-picker__add-media-menu__list-item"
          >
            <v-btn
              text
              color="accent"
              class="rounded-0 thumbnail-picker__action-button"
              elevation="0"
              @click="openStaticMediaPicker()"
              :disabled="uploadFile.length > 0 && !oneSourceOnly ? true : false"
            >
              <v-icon class="mr-1">bhi-image</v-icon
              ><span>Placeholder gallery</span>
            </v-btn>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <ml-file-picker-dialog
      :show-dialog="showMlFilePickerDialog"
      @close-dialog="closeMlFilePickerDialog"
      @file-picked="chosenFileMlFilePickerDialog"
      :allowedFileTypes="allowedFileTypes"
      v-on="$listeners"
    />
    <static-media-picker-dialog
      :show-dialog="showStaticMediaPicker"
      @close-dialog="closeStaticMediaPicker"
      @static-media-picked="chosenStaticMediaPicker"
    />
  </div>
</template>
<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import FileUpload from 'vue-upload-component'
import MlFilePickerDialog from '@/components/dialogs/MlFilePickerDialog.vue'
import globalPreviewMediaMixin from '@/mixins/globalPreviewMedia'
import StaticMediaPickerDialog from '@/components/dialogs/StaticMediaPickerDialog.vue'
import { FILE_SIZE_LIMIT, FILE_SIZE_LIMIT_NAME } from '@/helpers/constants'
import { getFileTypeByUrl } from '@/helpers/chat'

export default {
  name: 'ThumbnailPicker',
  components: {
    MlFilePickerDialog,
    StaticMediaPickerDialog,
    FileUpload,
  },
  mixins: [globalPreviewMediaMixin],
  props: {
    label: {
      type: String,
      default: 'Thumbnail',
    },
    currentFile: {
      type: [Object, null],
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    allowedFileTypes: {
      type: Array,
      default: () => [18, 19],
    },
    enableMobileStaticMedia: {
      type: Boolean,
      default: false,
    },
    currentMobileStaticMedia: {
      type: [Object, null],
      default: null,
    },
    forceMobileStaticMedia: {
      type: Boolean,
      default: false,
    },
    showAction: {
      type: Boolean,
      default: false,
    },
    oneSourceOnly: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    uploadFile: [],
    mlFile: null,
    showMlFilePickerDialog: false,
    currentFileRemoved: false,
    componentId: null,
    mobileStaticMedia: null,
    showStaticMediaPicker: false,
    isVideoType: false,
    isFileLoaded: true,
  }),
  mounted() {
    // Get unique id of component
    this.componentId = this._uid

    if (
      this.forceMobileStaticMedia &&
      !this.currentFile &&
      !this.currentMobileStaticMedia
    ) {
      this.randomizePlaceholder()
    }
  },
  watch: {
    currentFile(newVal) {
      // Reset currentFileRemoved when currentFile changes
      if (newVal) {
        this.currentFileRemoved = false
      }
    },
    currentMobileStaticMedia(newVal) {
      // Reset currentFileRemoved when currentMobileStaticMedia changes
      if (newVal) {
        this.currentFileRemoved = false
      }
    },
  },
  computed: {
    ...mapState('staticMediaStates', ['staticMedia']),
    ...mapGetters('dictionariesStates', ['getMimeTypes']),
    isFileSelected() {
      const isMobileStaticSelected =
        !this.forceMobileStaticMedia &&
        ((!this.currentFileRemoved && this.currentMobileStaticMedia) ||
          this.mobileStaticMedia)

      const result =
        !!isMobileStaticSelected ||
        this.mlFile !== null ||
        this.uploadFile?.[0] ||
        (this.currentFile !== null && !this.currentFileRemoved)

      return result
    },
    filePreview() {
      let fileTypeId = null

      if (this.uploadFile?.[0]?.file) {
        if (this.uploadFile?.[0]?.file?.type?.includes?.('pdf')) {
          fileTypeId = 20
        } else if (this.uploadFile?.[0]?.file?.type?.includes?.('video')) {
          fileTypeId = 19
        } else if (this.uploadFile?.[0]?.file?.type?.includes?.('image')) {
          fileTypeId = 18
          return URL.createObjectURL(this.uploadFile?.[0]?.file)
        } else {
          fileTypeId = 18
        }
      } else if (this.mlFile?.thumbnailUrl) {
        return this.mlFile?.thumbnailUrl
      } else if (
        (this.currentFileRemoved || !this.currentFile) &&
        this.mobileStaticMedia
      ) {
        return this.mobileStaticMedia.mediaUrl
      } else if (
        this.currentFile?.thumbnailUrl &&
        !this.currentFileRemoved &&
        !this.mobileStaticMedia
      ) {
        return this.currentFile.thumbnailUrl
      } else if (
        (this.currentFileRemoved || !this.currentFile) &&
        ((this.oneSourceOnly &&
          !this.currentFileRemoved &&
          this.currentMobileStaticMedia) ||
          (!this.oneSourceOnly && this.currentMobileStaticMedia))
      ) {
        return this.currentMobileStaticMedia.mediaUrl
      } else if (
        (this.currentFileRemoved || !this.currentFile) &&
        this.mobileStaticMedia
      ) {
        return this.mobileStaticMedia.mediaUrl
      } else if (this.mlFile?.fileTypeId) {
        fileTypeId = this.mlFile.fileTypeId
      } else if (this.currentFile?.fileTypeId && !this.currentFileRemoved) {
        fileTypeId = this.currentFile?.fileTypeId
      }

      return this.placeholder(fileTypeId)
    },
  },
  methods: {
    ...mapMutations('appStates', ['setSnackbarNotification']),
    randomizePlaceholder() {
      const randomIndex = Math.floor(Math.random() * this.staticMedia.length)
      const newMobileStaticMedia = this.staticMedia[randomIndex]
      this.chosenStaticMediaPicker(newMobileStaticMedia)
    },
    placeholder(fileTypeId) {
      let image = 'placeholder_other.png'

      switch (fileTypeId) {
        case 17:
          image = 'placeholder_other.png'
          break
        case 18:
          image = 'placeholder_image.png'
          break
        case 19:
          image = 'placeholder_video.png'
          break
        case 20:
          image = 'placeholder_pdf.png'
          break
        case 21:
          image = 'placeholder_doc.png'
          break
        case 22:
          image = 'placeholder_presentation.png'
          break
        case 23:
          image = 'placeholder_sheet.png'
          break
        default:
          break
      }
      return require('@/assets/' + image)
    },
    inputFile(newFile, oldFile) {
      if (newFile && !oldFile) {
        if (newFile.size < FILE_SIZE_LIMIT) {
          this.uploadFile = [newFile]
          this.fileChosen('file-picker', newFile)
        } else {
          this.setSnackbarNotification(
            `Max upload file size is ${FILE_SIZE_LIMIT_NAME}. Try again with smaller file.`,
          )
        }
      }
    },
    openMlFilePickerDialog() {
      this.showMlFilePickerDialog = true
    },
    closeMlFilePickerDialog() {
      this.showMlFilePickerDialog = false
    },
    chosenFileMlFilePickerDialog(id, name, rest) {
      this.showMlFilePickerDialog = false
      const mlFile = {
        id,
        name,
        ...rest,
      }
      this.mlFile = mlFile
      this.fileChosen('media-library', mlFile)
    },
    fileChosen(type, file) {
      if (this.oneSourceOnly) {
        this.currentFileRemoved = true
        if (type === 'media-library') {
          this.mobileStaticMedia = null
          this.uploadFile = []
        } else if (type === 'mobile-static-media') {
          this.mlFile = null
          this.uploadFile = []
        } else if (type === 'file-picker') {
          this.mobileStaticMedia = null
          this.mlFile = null
        }
      }

      this.$emit('thumbnail-picked', {
        type,
        file,
      })
    },
    selectLocalFile(name) {
      const formRef = this.$refs?.[name]
      if (!formRef) {
        console.warn('No file-upload ref')
        return
      }

      const inputRef = formRef?.$children?.find?.(
        el => el?.$el?.name?.toLowerCase?.() === 'file',
      )

      if (!inputRef) {
        console.warn('No input in file-upload ref')
        return
      }

      inputRef.$el.click()
    },
    removeFile() {
      const formRef = this.$refs?.[`upload-${this.componentId}`]
      formRef?.clear?.()
      this.mlFile = null
      this.uploadFile = []
      this.currentFileRemoved = true

      if (this.oneSourceOnly) {
        this.mobileStaticMedia = null
      }
      this.$emit('thumbnail-picked', null)
      if (!this.currentMobileStaticMedia && this.forceMobileStaticMedia) {
        this.randomizePlaceholder()
      }
    },
    showPreview() {
      let fileTypeId = null
      let previewUrl = null

      if (this.uploadFile?.[0]?.file) {
        if (this.uploadFile?.[0]?.file?.type?.includes?.('pdf')) {
          previewUrl = URL.createObjectURL(this.uploadFile?.[0]?.file)
          fileTypeId = 20
        } else if (this.uploadFile?.[0]?.file?.type?.includes?.('video')) {
          previewUrl = URL.createObjectURL(this.uploadFile?.[0]?.file)
          fileTypeId = 19
        } else {
          previewUrl = URL.createObjectURL(this.uploadFile?.[0]?.file)
          fileTypeId = 18
        }
      } else if (
        this.mlFile?.mediaUrl ||
        this.mlFile?.fileUrl ||
        this.mlFile?.previewUrl
      ) {
        previewUrl =
          this.mlFile?.mediaUrl ||
          this.mlFile?.fileUrl ||
          this.mlFile?.previewUrl
        fileTypeId = this.mlFile?.fileTypeId
      } else if (
        (this.currentFileRemoved || !this.currentFile) &&
        this.mobileStaticMedia
      ) {
        previewUrl = this.mobileStaticMedia.mediaUrl
        fileTypeId = 18
      } else if (
        (this.currentFile?.mediaUrl || this.currentFile?.thumbnailUrl) &&
        !this.currentFileRemoved
      ) {
        previewUrl =
          this.currentFile?.mediaUrl || this.currentFile?.thumbnailUrl
      } else if (
        (this.currentFileRemoved || !this.currentFile) &&
        ((this.oneSourceOnly &&
          !this.currentFileRemoved &&
          this.currentMobileStaticMedia) ||
          (!this.oneSourceOnly && this.currentMobileStaticMedia))
      ) {
        previewUrl = this.currentMobileStaticMedia.mediaUrl
        fileTypeId = 18
      }

      this.openGlobalPreviewMedia({
        mediaUrl: previewUrl,
        imageName: null,
        mlResourceUrl: null,
        fileTypeId: fileTypeId,
      })
    },
    openStaticMediaPicker() {
      this.showStaticMediaPicker = true
    },
    closeStaticMediaPicker() {
      this.showStaticMediaPicker = false
    },
    chosenStaticMediaPicker(staticMedia = null) {
      this.closeStaticMediaPicker()

      if (staticMedia === null) return

      this.currentFileRemoved = true
      this.mobileStaticMedia = staticMedia
      this.fileChosen('mobile-static-media', staticMedia)
    },
    onFilePreviewError() {
      // link expired
      if (
        this.currentFile.thumbnailUrl &&
        this.filePreview === this.currentFile.thumbnailUrl
      ) {
        this.$emit('refresh-current-file')
      }
    },
    onFilePreviewLoad() {
      this.isFileLoaded = false
      this.isVideoType = false

      const { type } = getFileTypeByUrl(this.currentFile?.mediaUrl)
      if (this.mlFile?.fileTypeId === 19 || type === 'video') {
        this.isVideoType = true
      }
    },
  },
}
</script>
<style lang="scss">
.thumbnail-picker {
  &__details {
    display: flex;
    align-items: center;
  }

  &__thumbnail {
    width: 64px;
    height: 64px;
    flex: initial;
    border-radius: 8px;
    object-fit: cover;
    display: block;

    &--selected {
      cursor: pointer;
    }
  }

  &__play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  }

  &__action-button {
    text-align: left;
    width: 100%;
    justify-content: flex-start;
    cursor: pointer !important;

    label {
      cursor: pointer;
    }

    &--upload:hover .v-btn::before {
      opacity: 0.08;
    }
  }
}
</style>
